import { useState, useEffect, useContext, useRef } from "react";

import moment from "moment";
import AuthContext from "../../lib/auth-context";

import throwMessage from "../../utils/throwMessage";

import { DataFetcher } from "../../utils/dataFetcher";
import { getJSON } from "../../utils/safeLocalStorage";
import { 
  getSelectedAccountId
} from "../../utils/accountUtils";
import { 
  getBacktestBalance
} from "../../utils/backtestAccountUtils";

const Logic = () => {
  const auth = useContext(AuthContext);

  const setBalances = () => {
    const user = getJSON("user", {});
    const selectedAccountId = getSelectedAccountId();
    
    // For backward compatibility, still support old balance fields
    const legacyBalances = {
      startingBalanceIC: parseFloat(user?.startingBalanceIC) || 0,
      startingBalancePC: parseFloat(user?.startingBalancePC) || 0
    };

    // Get balances from new account structure
    const accounts = user?.capitalAccounts || [];
    const accountBalances = {};
    
    accounts.forEach(account => {
      if (account.isActive) {
        accountBalances[account.id] = parseFloat(account.startingBalance) || 0;
      }
    });

    return {
      ...legacyBalances,
      accountBalances,
      selectedAccountId,
    };
  };

  const [openMacro, setOpenMacro] = useState(false);
  const [open, setOpen] = useState(false);
  const [openCal, setOpenCal] = useState(false);
  const [openNewTest, setOpenNewTest] = useState(false);
  const [openJournal, setOpenJournal] = useState(false);
  const [openForecast, setOpenForecast] = useState(false);
  const [forecastPrefilledData, setForecastPrefilledData] = useState(null);
  const [editBacktestMode, setEditBacktestMode] = useState(false);
  const [editBacktestData, setEditBacktestData] = useState(null);
  const [equityCurve, setEquityCurve] = useState(
    localStorage.getItem("equityCurve") === "true" ? true : false
  );

  const handleOpen = async () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    localStorage.setItem("pre-trade-checklist", false);
  };

  const handleCloseCal = (e) => {
    setOpenCal(false);
  };

  const handleOpenCal = () => {
    setOpenCal(true);
  };

  const handleOpenBacktest = () => {
    setEditBacktestMode(false);
    setEditBacktestData(null);
    setOpenNewTest(true);
  };
  const handleCloseBacktest = () => {
    setOpenNewTest(false);
    setEditBacktestMode(false);
    setEditBacktestData(null);
  };
  const handleOpenEditBacktest = (backtest) => {
    setEditBacktestMode(true);
    setEditBacktestData(backtest);
    setOpenNewTest(true);
  };

  const handleOpenForecast = (prefilledData = null) => {
    setForecastPrefilledData(prefilledData);
    setOpenForecast(true);
  };

  const handleCloseForecast = () => {
    setOpenForecast(false);
    setForecastPrefilledData(null);
  };

  const handleOpenJournal = (data) => {
    // Set backtest-active to the trade's testId so journal navigation works correctly
    // For missed trades, use "Missed Trades" instead of "All Missed Trades"
    if (data?.testId) {
      const testIdToSet = data.testId === "All Missed Trades" ? "Missed Trades" : data.testId;
      localStorage.setItem("backtest-active", testIdToSet);
    }
    
    // Set missed-trade filter if the trade is a missed trade
    if (data?.missedTrade) {
      localStorage.setItem("missed-trade", "Yes");
    } else {
      localStorage.setItem("missed-trade", "No");
    }
    
    if (data?.journal) {
      localStorage.setItem("journal-portal", JSON.stringify(data.journal));
      localStorage.setItem("trade-portal", JSON.stringify(data.trade));
      setOpenJournal(true);
    } else {
      const schema = {
        journalID: data.journalID,
        trade_id: data._id,
      };
      localStorage.setItem("journal-portal", JSON.stringify(schema));
      localStorage.setItem("trade-portal", JSON.stringify(data));
      setOpenJournal(true);
    }
  };

  const handleOpenMacro = () => {
    setOpenMacro(true);
  };

  const handleCloseMacro = () => {
    setOpenMacro(false);
  };
  const handleCloseJournal = () => {
    localStorage.removeItem("journal-portal");
    localStorage.removeItem("trade-portal");
    setOpenJournal(false);
  };

  const [remove, setRemove] = useState(false);

  let newTransactionIC;
  let noteIC = "";

  let newTransactionPC;
  let notePC = "";

  const [realisedPercentage, setRealisedPercentage] = useState(0);
  const [realisedReturn, setRealisedReturn] = useState(0);
  const [realisedReturnGross, setRealisedReturnGross] = useState(0);

  const [realisedPercentageBacktest, setRealisedPercentageBacktest] =
    useState(0);

  const [realisedPercentageWithFees, setRealisedPercentageWithFees] =
    useState(0);
  const [
    realisedPercentageWithFeesBacktest,
    setRealisedPercentageWithFeesBacktest,
  ] = useState(0);

  const [realisedReturnBacktest, setRealisedReturnBacktest] = useState(0);
  const [realisedReturnGrossBacktest, setRealisedReturnGrossBacktest] = useState(0);

  const [selectedArchivedTests, setSelectedArchivedTests] = useState("");
  const [allbackTestSchemas, setAllbackTestSchemas] = useState([]);
  const [selectedArchivedTestsBoolean, setSelectedArchivedTestsBoolean] =
    useState(false);
  const [showSpecficTest, setShowSpecficTest] = useState(localStorage.getItem("backtest-active") || "");
  const [toRemove, setToRemove] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const balanceData = setBalances();
  let startingBalanceIC = balanceData.startingBalanceIC;
  let startingBalancePC = balanceData.startingBalancePC;

  // eslint-disable-next-line no-unused-vars
  let balanceIC = startingBalanceIC;
  // eslint-disable-next-line no-unused-vars
  let balancePC = startingBalancePC;


  let virtualFields = [];
  let returnPercentTotalsPC = [];
  let returnPercentTotalsIC = [];
  let returnPercentTotalsPCWithFees = [];
  let returnPercentTotalsICWithFees = [];
  let returnDollarTotalsPersonal = [];
  let returnDollarTotalsInvestor = [];

  const effectRan = useRef(false);

  useEffect(() => {
    // generateRandomTrade("live", 20, "", false);
    // generateRandomTrade("backtest", 20, "Head n Shoulders", false);

    if (effectRan.current === false) {
      localStorage.setItem("missed-trade", "No");

      if (localStorage.getItem("trade-to-view")) {
        getTrades(localStorage.getItem("trade-to-view"));
      } else {
        // If no specific trade to view, clear any stale linked-trade-ids filter
        localStorage.removeItem("linked-trade-ids");
        getTrades();
      }
      setAllbackTestSchemas(auth.backtests);

      return () => {
        effectRan.current = true;
        localStorage.removeItem("trade-to-view");
        localStorage.removeItem("linked-trade-ids");
        // Don't remove backtest-active on unmount to preserve selection when switching tabs
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle backtesting mode changes
  useEffect(() => {
    if (auth.backtestingMode) {
      // When entering backtesting mode, restore the selected test from localStorage
      const backtestActive = localStorage.getItem("backtest-active");
      if (backtestActive) {
        setShowSpecficTest(backtestActive);
      }
    } else {
      // When leaving backtesting mode, clear the selected test and recalculate
      setShowSpecficTest("");
      localStorage.removeItem("backtest-active");
      // Force recalculation with a small delay to ensure state updates propagate
      setTimeout(() => {
        if (auth.trades && auth.trades.length > 0) {
          const previousAccountId = localStorage.getItem("pre-backtest-account-id");
          const accountId = previousAccountId || balanceData.selectedAccountId;
          calculateFields(auth.trades, false, undefined, undefined, undefined, accountId);
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.backtestingMode]);

  // Trigger backtest filtering when showSpecficTest changes (e.g., on page refresh)
  useEffect(() => {
    if (showSpecficTest && auth.backtestingMode) {
      if (showSpecficTest === "Missed Trades") {
        // Handle Missed Trades special case
        localStorage.setItem("backtest-active", "Missed Trades");
        const missedTrades = auth.trades.filter(t => t.missedTrade === true);
        calculateFields(missedTrades, true, "Missed Trades", undefined, undefined, balanceData.selectedAccountId);
      } else {
        // Handle regular backtest
        const selectedBacktest = allbackTestSchemas.find(t => t.testId === showSpecficTest);
        if (selectedBacktest) {
          showBacktestTrades(selectedBacktest);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSpecficTest, allbackTestSchemas, auth.backtestingMode]);

  const transactionsCalulator = async (trades, trade, index) => {
    const user = getJSON("user", {});
    const capitalAccounts = user?.capitalAccounts || [];

    // Get the previous trade's exit time (or epoch if first trade)
    let previous_trade_time;
    if (index === 0) {
      previous_trade_time = new Date(0);
    } else {
      const prevTrade = trades[index - 1];
      // Use exit date/time if available, otherwise use created timestamp
      if (prevTrade.exitDate && prevTrade.exitTime) {
        const [day, month, year] = prevTrade.exitDate.split('/');
        previous_trade_time = new Date(`20${year}-${month}-${day}T${prevTrade.exitTime}`);
      } else {
        previous_trade_time = new Date(prevTrade.created);
      }
    }

    // Get current trade's entry time
    let current_trade_time;
    if (trade.entryDate && trade.entryTime) {
      const [day, month, year] = trade.entryDate.split('/');
      current_trade_time = new Date(`20${year}-${month}-${day}T${trade.entryTime}`);
    } else {
      current_trade_time = new Date(trade.created);
    }

    let schemaIC = [];
    let schemaPC = [];

    capitalAccounts.forEach(account => {
      if (!account.isActive || !account.transactions) return;
      
      account.transactions.forEach(transaction => {
        const amount = parseFloat(transaction.amount);
        const note = transaction.note || "";
        const type = transaction.type;
        const transactionDate = new Date(transaction.date);

        // Include transactions between previous trade exit and current trade entry
        if (transactionDate > previous_trade_time && transactionDate <= current_trade_time) {
          if (account.type === "Investor") {
            schemaIC.push({
              amount: amount,
              note: note,
              reason: "Investor",
              type: type,
            });
          } else if (account.type === "Personal") {
            schemaPC.push({
              amount: amount,
              note: note,
              reason: "Personal",
              type: type,
            });
          }
        }
      });
    });

    let totalAmountIC = 0;

    schemaIC.forEach((transaction) => {
      noteIC = `${noteIC} ${transaction.note}`.trim();
      if (transaction.type === "withdrawal" || transaction.type === "payout") {
        totalAmountIC -= transaction.amount;
        balanceIC -= transaction.amount;
      } else if (transaction.type === "deposit") {
        totalAmountIC += transaction.amount;
        balanceIC += transaction.amount;
      }
    });

    if (totalAmountIC !== 0) {
      newTransactionIC =
        totalAmountIC > 0
          ? `+ ${totalAmountIC}`
          : `- ${Math.abs(totalAmountIC)}`;
    }

    let totalAmountPC = 0;

    schemaPC.forEach((transaction) => {
      notePC = `${notePC} ${transaction.note}`.trim();
      if (transaction.type === "withdrawal" || transaction.type === "payout") {
        totalAmountPC -= transaction.amount;
        balancePC -= transaction.amount;
      } else if (transaction.type === "deposit") {
        totalAmountPC += transaction.amount;
        balancePC += transaction.amount;
      }
    });

    if (totalAmountPC !== 0) {
      newTransactionPC =
        totalAmountPC > 0
          ? `+ ${totalAmountPC}`
          : `- ${Math.abs(totalAmountPC)}`;
    }
  };
  const getTrades = (spefic) => {
    // Check if we're coming from a forecast view with multiple linked trades
    const linkedTradeIds = localStorage.getItem("linked-trade-ids");
    if (linkedTradeIds) {
      const tradeIds = JSON.parse(linkedTradeIds);
      const linkedTrades = auth.trades.filter((trade) => tradeIds.includes(trade._id));
      
      // Set equity mode and year filter based on the first linked trade
      if (linkedTrades.length > 0) {
        const firstTrade = linkedTrades[0];
        if (firstTrade.equityType === "Personal Capital") {
          localStorage.setItem("equity-mode", "Personal Capital");
          auth.setEquityTypeMode("Personal Capital");
        } else if (firstTrade.equityType === "Investor Capital") {
          localStorage.setItem("equity-mode", "Investor Capital");
          auth.setEquityTypeMode("Investor Capital");
        }
        
        // Set year filter to match the trade's year
        if (firstTrade.entryDate) {
          const dateArray = firstTrade.entryDate.split("/");
          let tradeYear = dateArray[2];
          // Convert 2-digit year to 4-digit year (e.g., 25 -> 2025)
          if (tradeYear && tradeYear.length === 2) {
            tradeYear = "20" + tradeYear;
          }
          localStorage.setItem("year", tradeYear);
          // Dispatch event to update stats bar
          window.dispatchEvent(new CustomEvent("year-changed", { detail: { year: tradeYear } }));
        }
      }

      if (auth.backtestingMode) {
        // Get testId from the first linked trade
        const firstTrade = linkedTrades[0];
        const testId = firstTrade.missedTrade ? "Missed Trades" : firstTrade.testId;
        localStorage.setItem("backtest-active", testId);
        setShowSpecficTest(testId);
        setAllbackTestSchemas(auth.backtests);
        // Pass ALL trades to calculateFields, the grid will filter by linked-trade-ids
        calculateFields(auth.trades, true, testId, undefined, undefined, balanceData.selectedAccountId);
      } else {
        // Pass ALL trades to calculateFields, the grid will filter by linked-trade-ids
        calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
      }
      
      return;
    }

    if (spefic) {
      // find spefic trade
      let singleTrade = auth.trades.find((trade) => trade._id.includes(spefic));

      if (singleTrade && singleTrade.equityType === "Personal Capital") {
        localStorage.setItem("equity-mode", "Personal Capital");
        auth.setEquityTypeMode("Personal Capital");
      } else if (singleTrade && singleTrade.equityType === "Investor Capital") {
        localStorage.setItem("equity-mode", "Investor Capital");
        auth.setEquityTypeMode("Investor Capital");
      }
      
      // Set year filter to match the trade's year
      if (singleTrade && singleTrade.entryDate) {
        const dateArray = singleTrade.entryDate.split("/");
        let tradeYear = dateArray[2];
        // Convert 2-digit year to 4-digit year (e.g., 25 -> 2025)
        if (tradeYear && tradeYear.length === 2) {
          tradeYear = "20" + tradeYear;
        }
        localStorage.setItem("year", tradeYear);
        // Dispatch event to update stats bar
        window.dispatchEvent(new CustomEvent("year-changed", { detail: { year: tradeYear } }));
      }

      if (auth.backtestingMode && singleTrade) {
        getBacktests(singleTrade);
      } else if (!auth.backtestingMode && singleTrade) {
        // Pass ALL trades to calculateFields, it will filter to the single trade
        calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
      }
    } else {
      if (auth.backtestingMode) {
        showBacktestTrades({ testId: localStorage.getItem("backtest-active") });
      } else {
        calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
      }
    }
  };
  //-----------------------------------------------------------------------------------------//
  // NOTE: Calculation logic has been moved to server-side centralized service
  // All calculations now happen in /server/lib/tradeCalculations.js
  // Trades use pre-calculated DB fields for performance
  //-----------------------------------------------------------------------------------------//
  const handleEditCell = async (cell) => {
    if (!cell.id) {
      throwMessage("error", "Error Try Again");
      return;
    }

    // Optimistic update: immediately update the UI
    const tradeIndex = auth.allTrades.findIndex((t) => t.id === cell.id);
    if (tradeIndex !== -1) {
      const updatedTrades = [...auth.allTrades];
      updatedTrades[tradeIndex] = {
        ...updatedTrades[tradeIndex],
        [cell.field]: cell.value
      };
      auth.setAllTrades(updatedTrades);
    }

    // Send to server in background
    try {
      const result = await DataFetcher("edit-trade", cell);
      if (result) {
        // Update the main trades array
        const filteredArray = auth.trades.filter((x) => x._id !== result._id);
        filteredArray.push(result);
        filteredArray.sort((a, b) => {
          return moment(a.created).diff(b.created);
        });
        auth.setTrades(filteredArray);

        // Small delay to allow optimistic update to render before recalculation
        setTimeout(() => {
          // Recalculate with server-confirmed data
          if (!auth.backtestingMode) {
            calculateFields(filteredArray, false, undefined, undefined, undefined, balanceData.selectedAccountId);
          } else {
            calculateFields(filteredArray, true, result.testId, undefined, undefined, balanceData.selectedAccountId);
          }
        }, 50);
      } else {
        throwMessage("error", "Error Try Again");
        // Revert optimistic update on error
        if (!auth.backtestingMode) {
          calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
        } else {
          const testId = auth.allTrades[tradeIndex]?.testId;
          calculateFields(auth.trades, true, testId, undefined, undefined, balanceData.selectedAccountId);
        }
      }
    } catch (error) {
      throwMessage("error", "Error Try Again");
      // Revert optimistic update on error
      if (!auth.backtestingMode) {
        calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
      } else {
        const testId = auth.allTrades[tradeIndex]?.testId;
        calculateFields(auth.trades, true, testId, undefined, undefined, balanceData.selectedAccountId);
      }
    }
  };
  //-----------------------------------------------------------------------------------------//
  const calculateFields = async (
    _allTrades,
    backtest,
    testId,
    rotar,
    equityCurve,
    selectedAccountId
  ) => {
    if (_allTrades === undefined) {
      return;
    }

    // Reset balances and accumulators for each calculation run
    let startingBalanceIC, startingBalancePC;
    
    if (backtest) {
      // Use isolated backtesting account for backtest mode
      const backtestBalance = getBacktestBalance();
      startingBalanceIC = 0; // Not used in backtest mode
      startingBalancePC = backtestBalance;
      // eslint-disable-next-line no-unused-vars
      balanceIC = 0;
      // eslint-disable-next-line no-unused-vars
      balancePC = backtestBalance;
    } else {
      // Use live account balances for live mode (unchanged)
      const balances = setBalances();
      startingBalanceIC = balances.startingBalanceIC;
      startingBalancePC = balances.startingBalancePC;
      // eslint-disable-next-line no-unused-vars
      balanceIC = startingBalanceIC;
      // eslint-disable-next-line no-unused-vars
      balancePC = startingBalancePC;
    }


    virtualFields = [];
    returnPercentTotalsPC = [];
    returnPercentTotalsIC = [];
    returnPercentTotalsPCWithFees = [];
    returnPercentTotalsICWithFees = [];
    returnDollarTotalsPersonal = [];
    returnDollarTotalsInvestor = [];

    let trades = _allTrades;
    let allTradesForBalance = _allTrades; // Store all trades for balance calculation
    let profitLossDollarPersonal;
    let profitLossDollarInvestor;
    let rollingBalanceIC;
    let rollingBalancePC;
    let rollingBalanceICGross;
    let rollingBalancePCGross;

    let rollingPercentICGross;
    let rollingPercentICNet;
    let rollingPercentPCGross;
    let rollingPercentPCNet;

    if (!backtest && localStorage.getItem("year") !== "0") {
      const currentYearSet = moment(localStorage.getItem("year")).format("YY");

      trades = trades.filter((t, index) => {
        let dateInput = t.entryDate; // DD/MM/YY format

        let dateArray = dateInput.split("/");

        let newDate = dateArray[2];

        if (Number(newDate) === Number(currentYearSet)) {
          return t;
        } else {
          return false;
        }
      });

      let tradesBeforeYear = _allTrades.filter((t) => {
        if (!t.entryDate) return false;
        let dateArray = t.entryDate.split("/");
        let tradeYear = dateArray[2];
        return Number(tradeYear) < Number(currentYearSet);
      });

      if (!backtest) {
        tradesBeforeYear = tradesBeforeYear.filter((tests) => tests?.id?.includes("live-"));
        
        if (selectedAccountId && selectedAccountId !== "All") {
          tradesBeforeYear = tradesBeforeYear.filter((trade) => {
            if (selectedAccountId === "All") {
              return true;
            } else if (selectedAccountId === "All Personal") {
              return trade.equityType === "Personal Capital";
            } else if (selectedAccountId === "All Investor") {
              return trade.equityType === "Investor Capital";
            } else {
              return trade.accountId === selectedAccountId;
            }
          });
        }
      } else {
        tradesBeforeYear = tradesBeforeYear.filter((tests) => tests?.testId?.includes(testId));
      }

      tradesBeforeYear.sort((a, b) => moment(a.created).diff(b.created));

      tradesBeforeYear.forEach((trade) => {
        if (trade.equityType === "Personal Capital" && trade.profitLossDollarPersonal) {
          balancePC += trade.netDollarPC || parseFloat(trade.profitLossDollarPersonal);
        }
        if (trade.equityType === "Investor Capital" && trade.profitLossDollarInvestor) {
          balanceIC += trade.netDollarIC || parseFloat(trade.profitLossDollarInvestor);
        }
      });
    }

    if (backtest) {
      trades = trades
        .filter((tests) => tests?.testId?.includes(testId))
        .sort((a, b) => {
          return moment(a.created).diff(b.created);
        });

      // trades = _allTrades.filter((trades) => trades?.equityType === "Backtest");

      if (trades.length === 0) {
        setRealisedPercentageBacktest(0);
        setRealisedReturnBacktest(0);
      }
    } else {
      trades = trades
        .filter((tests) => tests?.id?.includes("live-"))
        .sort((a, b) => {
          return moment(a.created).diff(b.created);
        });

      // Filter by account ID (new logic)
      if (selectedAccountId && selectedAccountId !== "All") {
        trades = trades.filter((trade) => {
          if (selectedAccountId === "All") {
            return true;
          } else if (selectedAccountId === "All Personal") {
            return trade.equityType === "Personal Capital";
          } else if (selectedAccountId === "All Investor") {
            return trade.equityType === "Investor Capital";
          } else {
            // Filter by specific account ID
            return trade.accountId === selectedAccountId;
          }
        });
      } 

      if (trades.length === 0) {
        setRealisedPercentage(0);
        setRealisedReturn(0);

        auth.setRealisedBalanceIC(0);
        auth.setRealisedBalancePC(0);
      }
    }

    if (trades.length === 0) {
      auth.setAllTrades([]);
    }

    for (let index = 0; index < trades.length; index++) {
      const trade = trades[index];
      const capital = trade.equityType;

      transactionsCalulator(trades, trade, index);

      if (capital === "Investor Capital") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippageIC = trade.outcomeIC;
        const profitLossPercentageIC = parseFloat(trade.profitLossPercentageIC || 0);
        const percentWithFeesIC = parseFloat(trade.percentWithFeesIC || 0);

        profitLossDollarInvestor = parseFloat(trade.profitLossDollarInvestor || 0);

        returnPercentTotalsIC.push(profitLossPercentageIC);
        returnPercentTotalsICWithFees.push(percentWithFeesIC);
        returnDollarTotalsInvestor.push(profitLossDollarInvestor);

        // Use DB-calculated rolling balances
        rollingBalanceIC = parseFloat(trade.rollingBalanceIC || 0);
        rollingBalanceICGross = parseFloat(trade.rollingBalanceICGross || 0);
        rollingPercentICGross = parseFloat(trade.rollingPercentICGross || 0);
        rollingPercentICNet = parseFloat(trade.rollingPercentICNet || 0);

        const newTrade_virtual = {
          rollingBalanceIC: rollingBalanceIC,
          rollingBalanceICGross: rollingBalanceICGross,
          rollingPercentICGross: rollingPercentICGross,
          rollingPercentICNet: rollingPercentICNet,
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: profitLossDollarInvestor,
          profitLossDollarPersonal: 0,
          profitLossPercentagePC: 0,
          profitLossPercentageIC: parseFloat(profitLossPercentageIC),
          percentWithFeesPC: 0,
          percentWithFeesIC: parseFloat(percentWithFeesIC),
          // rr: riskToRewardDecimal,
          outcomePC: "",
          outcomeIC: outcomeOfSlippageIC,
          openPricePC: 0,
          closePricePC: 0,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          direction: trade.direction,
          tradingStyle: trade.tradingStyle,
          stopLossPC: 0,
          stopLossIC: trade.stopLossIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          duration: "",
          foreCastId: trade.foreCastId,
          dxyHourlyBefore: trade.dxyHourlyBefore,
          dxyHourlyAfter: trade.dxyHourlyAfter,
          dxyDailyBefore: trade.dxyDailyBefore,
          dxyDailyAfter: trade.dxyDailyAfter,
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          newTransactionIC: newTransactionIC,
          transanctionNotesIC: noteIC,
          rollingFeesPC: 0,
          rollingFeesIC: parseFloat(trade.rollingFeesIC || 0),
          returnTotalsPersonal: 0,
          returnTotalsPersonalGross: 0,
          returnTotalsPersonalNet: 0,
          returnTotalsInvestor: parseFloat(trade.returnTotalsInvestor || 0),
          returnTotalsInvestorGross: parseFloat(trade.returnTotalsInvestorGross || 0),
          returnTotalsInvestorNet: parseFloat(trade.returnTotalsInvestorNet || 0),
          created: trade.created,
          equityType: trade.equityType,
          lotsPC: trade.lotsPC,
          lotsIC: trade.lotsIC,
          unitsPC: trade.unitsPC,
          unitsIC: trade.unitsIC,
          moneyPC: trade.moneyPC,
          moneyIC: trade.moneyIC,
          stopLossPricePC: trade.stopLossPricePC,
          stopLossPriceIC: trade.stopLossPriceIC,
          duplicatedFrom: trade.duplicatedFrom,
          accountId: trade.accountId,
          risk: trade.risk,
          executionDiscipline: trade.executionDiscipline,
          emotionalControl: trade.emotionalControl,
          setupQuality: trade.setupQuality,
          entryTimeframe: trade.entryTimeframe,
          netDollarPC: 0,
          netDollarIC: trade.netDollarIC || 0,
        };

        virtualFields.push(newTrade_virtual);

        virtualFields = virtualFields.filter(
          (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
        );

        //---------------------------------------------//
      } else if (capital === "Personal Capital" || capital === "Backtest") {
        // Use DB-calculated fields (all trades have these after migration)
        const outcomeOfSlippagePC = trade.outcomePC;
        const profitLossPercentagePC = parseFloat(trade.profitLossPercentagePC || 0);
        const percentWithFeesPC = parseFloat(trade.percentWithFeesPC || 0);

        profitLossDollarPersonal = parseFloat(trade.profitLossDollarPersonal || 0);

        returnPercentTotalsPC.push(profitLossPercentagePC);
        returnPercentTotalsPCWithFees.push(percentWithFeesPC);
        returnDollarTotalsPersonal.push(profitLossDollarPersonal);

        // Use DB-calculated rolling balances
        rollingBalancePC = parseFloat(trade.rollingBalancePC || 0);
        rollingBalancePCGross = parseFloat(trade.rollingBalancePCGross || 0);
        rollingPercentPCGross = parseFloat(trade.rollingPercentPCGross || 0);
        rollingPercentPCNet = parseFloat(trade.rollingPercentPCNet || 0);

        const newTrade_virtual = {
          rollingBalancePC: rollingBalancePC,
          rollingBalancePCGross: rollingBalancePCGross,
          rollingPercentPCGross: rollingPercentPCGross,
          rollingPercentPCNet: rollingPercentPCNet,
          timezone: trade.timezone,
          id: trade.id,
          _id: trade._id,
          profitLossDollarInvestor: 0,
          profitLossDollarPersonal: profitLossDollarPersonal,
          profitLossPercentagePC: parseFloat(profitLossPercentagePC),
          profitLossPercentageIC: 0,
          percentWithFeesPC: parseFloat(percentWithFeesPC),
          percentWithFeesIC: 0,
          outcomePC: outcomeOfSlippagePC,
          outcomeIC: "",
          openPricePC: trade.openPricePC,
          closePricePC: trade.closePricePC,
          openPriceIC: trade.openPriceIC,
          closePriceIC: trade.closePriceIC,
          pair: trade.pair,
          direction: trade.direction,
          tradingStyle: trade.tradingStyle,
          stopLossPC: trade.stopLossPC,
          stopLossIC: trade.stopLossIC,
          entryDate: trade.entryDate,
          entryTime: trade.entryTime,
          exitDate: trade.exitDate,
          exitTime: trade.exitTime,
          feePC: trade.feePC,
          feeIC: trade.feeIC,
          swapPC: trade.swapPC,
          swapIC: trade.swapIC,
          betaPattern: trade.betaPattern,
          alphaPattern: trade.alphaPattern,
          
          duration: "",
          foreCastId: trade.foreCastId,
          dxyHourlyBefore: trade.dxyHourlyBefore,
          dxyHourlyAfter: trade.dxyHourlyAfter,
          dxyDailyBefore: trade.dxyDailyBefore,
          dxyDailyAfter: trade.dxyDailyAfter,
          min5_before: trade.min5_before,
          min15_before: trade.min15_before,
          hour1_before: trade.hour1_before,
          hour1_after: trade.hour1_after,
          hour4_before: trade.hour4_before,
          hour4_after: trade.hour4_after,
          daily_before: trade.daily_before,
          daily_after: trade.daily_after,
          weekly_before: trade.weekly_before,
          weekly_after: trade.weekly_after,
          journalComplete: trade.journalComplete,
          journalID: trade.journalID,
          entryType: trade.entryType,
          grade: trade.grade,
          highlightedPair: trade.highlightedPair,
          firstEntryOrScaleIn: trade.firstEntryOrScaleIn,
          testId:
            trade.missedTrade ? "All Missed Trades" : trade.testId,
          missedTrade: trade.missedTrade,
          rollingFeesPC: parseFloat(trade.rollingFeesPC || 0),
          rollingFeesIC: 0,
          returnTotalsPersonal: parseFloat(trade.returnTotalsPersonal || 0),
          returnTotalsPersonalGross: parseFloat(trade.returnTotalsPersonalGross || 0),
          returnTotalsPersonalNet: parseFloat(trade.returnTotalsPersonalNet || 0),
          returnTotalsInvestor: 0,
          returnTotalsInvestorGross: 0,
          returnTotalsInvestorNet: 0,
          created: trade.created,
          equityType: trade.equityType,
          newTransactionPC: newTransactionPC,
          transanctionNotesPC: notePC,
          lotsPC: trade.lotsPC,
          lotsIC: trade.lotsIC,
          unitsPC: trade.unitsPC,
          unitsIC: trade.unitsIC,
          moneyPC: trade.moneyPC,
          moneyIC: trade.moneyIC,
          stopLossPricePC: trade.stopLossPricePC,
          stopLossPriceIC: trade.stopLossPriceIC,
          duplicatedFrom: trade.duplicatedFrom,
          accountId: trade.accountId,
          risk: trade.risk,
          executionDiscipline: trade.executionDiscipline,
          emotionalControl: trade.emotionalControl,
          setupQuality: trade.setupQuality,
          entryTimeframe: trade.entryTimeframe,
          netDollarPC: trade.netDollarPC || 0,
          netDollarIC: 0,
        };

        virtualFields.push(newTrade_virtual);

        virtualFields = virtualFields.filter(
          (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
        );

        //---------------------------------------------//
      }
    
      //---------------------------------------------//
      newTransactionPC = undefined;
      notePC = "";
      newTransactionIC = undefined;
      noteIC = "";
    } // end of Alltrades for loop

    // Update UI with all processed trades once after loop completes
    if (!rotar && !equityCurve) {
      // If viewing linked trades from forecast, keep ALL trades in allTrades
      // The grid will filter by linked-trade-ids
      const linkedTradeIds = localStorage.getItem("linked-trade-ids");
      const tradeToView = localStorage.getItem("trade-to-view");
      
      if (linkedTradeIds) {
        auth.setAllTrades(virtualFields);
      } else if (tradeToView) {
        // Viewing a single specific trade - keep ALL trades, grid will filter
        auth.setAllTrades(virtualFields);
      } else if (localStorage.getItem("inprogress-mode") === "true") {
        const inProgressTrades = virtualFields.filter((trade) =>
          trade.outcomeIC?.includes("In Progress") || trade.outcomePC?.includes("In Progress")
        );
        auth.setAllTrades(inProgressTrades);
      } else {
        const completedTrades = virtualFields.filter(
          (trade) => !trade.outcomeIC?.includes("In Progress") && !trade.outcomePC?.includes("In Progress")
        );
        auth.setAllTrades(completedTrades);
      }
    }

    // Calculate current balance using ALL trades (not filtered by year)
    let currentBalancePC = startingBalancePC;
    let currentBalanceIC = startingBalanceIC;
    

    // Filter all trades for balance calculation
    let balanceTrades = allTradesForBalance;
    if (backtest) {
      balanceTrades = balanceTrades.filter((tests) => tests?.testId?.includes(testId));
    } else {
      balanceTrades = balanceTrades.filter((tests) => tests?.id?.includes("live-"));
      
      // Filter by selected account
      if (selectedAccountId && selectedAccountId !== "All") {
        if (selectedAccountId === "All Personal") {
          balanceTrades = balanceTrades.filter((t) => t?.equityType === "Personal Capital");
        } else if (selectedAccountId === "All Investor") {
          balanceTrades = balanceTrades.filter((t) => t?.equityType === "Investor Capital");
        } else {
          // Filter by specific account ID
          balanceTrades = balanceTrades.filter((t) => t?.accountId === selectedAccountId);
        }
      }
    }

    // Calculate final balance from filtered trades
    balanceTrades.sort((a, b) => moment(a.created).diff(b.created)).forEach((trade) => {
      if (trade.equityType === "Personal Capital" && trade.profitLossDollarPersonal) {
        currentBalancePC += trade.netDollarPC || parseFloat(trade.profitLossDollarPersonal);
      }
      if (trade.equityType === "Investor Capital" && trade.profitLossDollarInvestor) {
        currentBalanceIC += trade.netDollarIC || parseFloat(trade.profitLossDollarInvestor);
      }
    });

    auth.setRealisedBalancePC(currentBalancePC);
    localStorage.setItem("balancePC", currentBalancePC);
    auth.setRealisedBalanceIC(currentBalanceIC);
    localStorage.setItem("balanceIC", currentBalanceIC);
  

    const runRotaterCals = () => {
      const rotater = rotar ? rotar : localStorage.getItem("rotater");
      
      // Determine if we're dealing with Personal, Investor, or mixed trades
      let trades = virtualFields;
      const mapFromColors = new Map(trades.map((c) => [c.id, c]));
      trades = [...mapFromColors.values()];
      
      // Check what types of trades we have - use ONLY the actual trade types, ignore equity-mode
      const hasPersonal = trades.some(t => t.equityType === "Personal Capital" || t.equityType === "Backtest");
      const hasInvestor = trades.some(t => t.equityType === "Investor Capital");
      
      // Use the actual trade types, not the legacy equity-mode
      const personal = hasPersonal && !hasInvestor;
      const investor = hasInvestor && !hasPersonal;

      setRealisedReturn(0);
      setRealisedReturnGross(0);
      setRealisedPercentage(0);
      setRealisedReturnBacktest(0);
      setRealisedReturnGrossBacktest(0);
      setRealisedPercentageBacktest(0);
      setRealisedPercentageWithFees(0);
      setRealisedPercentageWithFeesBacktest(0);

      const last90Days = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        const currentDate = moment();

        trades.forEach((t) => {
          const tradeExitMoment = moment(t.exitDate + " " + t.exitTime, "DD/MM/YY HH:mm");
          const daysDiff = currentDate.diff(tradeExitMoment, "days");
          
          if (daysDiff <= 90 && daysDiff >= 0) {
            if (personal) {
              percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
              percentNet.push(parseFloat(t.percentWithFeesPC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
              // Calculate net from gross + fees + swaps (server logic: netDollarPC = profitLossDollarPersonal + feePC + swapPC)
              const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
              dollarNet.push(netDollar);
            } else if (investor) {
              percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
              percentNet.push(parseFloat(t.percentWithFeesIC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
              // Calculate net from gross + fees + swaps (server logic: netDollarIC = profitLossDollarInvestor + feeIC + swapIC)
              const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
              dollarNet.push(netDollar);
            }
          }
        });
        
        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };
      const last30Days = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        const currentDate = moment();
        
        trades.forEach((t) => {
          const tradeExitMoment = moment(t.exitDate + " " + t.exitTime, "DD/MM/YY HH:mm");
          const daysDiff = currentDate.diff(tradeExitMoment, "days");
          
          if (daysDiff <= 30 && daysDiff >= 0) {
            if (personal) {
              percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
              percentNet.push(parseFloat(t.percentWithFeesPC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
              const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
              dollarNet.push(netDollar);
            } else if (investor) {
              percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
              percentNet.push(parseFloat(t.percentWithFeesIC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
              const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
              dollarNet.push(netDollar);
            }
          }
        });

        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };
      const last7Days = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        
        trades.forEach((t) => {
          if (
            moment().diff(
              new Date(moment(t.exitDate + " " + t.exitTime, "DD/MM/YY HH:mm")),
              "days"
            ) <= 7
          ) {
            if (personal) {
              percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
              percentNet.push(parseFloat(t.percentWithFeesPC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
              const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
              dollarNet.push(netDollar);
            } else if (investor) {
              percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
              percentNet.push(parseFloat(t.percentWithFeesIC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
              const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
              dollarNet.push(netDollar);
            }
          }
        });
        
        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };
      const last24Hours = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        
        trades.forEach((t) => {
          if (
            moment().diff(
              new Date(moment(t.exitDate + " " + t.exitTime, "DD/MM/YY HH:mm")),
              "hours"
            ) <= 24
          ) {
            if (personal) {
              percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
              percentNet.push(parseFloat(t.percentWithFeesPC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
              const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
              dollarNet.push(netDollar);
            } else if (investor) {
              percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
              percentNet.push(parseFloat(t.percentWithFeesIC || 0));
              dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
              const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
              dollarNet.push(netDollar);
            }
          }
        });
        
        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };
      const thisYear = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        
        // trades are already filtered by year in calculateFields, so just sum them all
        trades.forEach((t) => {
          if (personal) {
            percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
            percentNet.push(parseFloat(t.percentWithFeesPC || 0));
            dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
            const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
            dollarNet.push(netDollar);
          } else if (investor) {
            percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
            percentNet.push(parseFloat(t.percentWithFeesIC || 0));
            dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
            const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
            dollarNet.push(netDollar);
          }
        });
        
        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };
      const allTime = () => {
        const percentGross = [];
        const percentNet = [];
        const dollarGross = [];
        const dollarNet = [];
        
        trades.forEach((t) => {
          if (personal) {
            percentGross.push(parseFloat(t.profitLossPercentagePC || 0));
            percentNet.push(parseFloat(t.percentWithFeesPC || 0));
            dollarGross.push(parseFloat(t.profitLossDollarPersonal || 0));
            const netDollar = t.netDollarPC !== undefined ? t.netDollarPC : (parseFloat(t.profitLossDollarPersonal || 0) + parseFloat(t.feePC || 0) + parseFloat(t.swapPC || 0));
            dollarNet.push(netDollar);
          } else if (investor) {
            percentGross.push(parseFloat(t.profitLossPercentageIC || 0));
            percentNet.push(parseFloat(t.percentWithFeesIC || 0));
            dollarGross.push(parseFloat(t.profitLossDollarInvestor || 0));
            const netDollar = t.netDollarIC !== undefined ? t.netDollarIC : (parseFloat(t.profitLossDollarInvestor || 0) + parseFloat(t.feeIC || 0) + parseFloat(t.swapIC || 0));
            dollarNet.push(netDollar);
          }
        });
        
        const totalDollarGross = dollarGross.reduce((a, b) => a + b, 0);
        const totalDollarNet = dollarNet.reduce((a, b) => a + b, 0);
        
        // Calculate percentages from cumulative dollars
        const startingBalance = personal ? startingBalancePC : startingBalanceIC;
        const totalPercentGross = startingBalance !== 0 ? (totalDollarGross / startingBalance) * 100 : 0;
        const totalPercentNet = startingBalance !== 0 ? (totalDollarNet / startingBalance) * 100 : 0;

        setRealisedPercentage(totalPercentGross);
        setRealisedReturn(totalDollarNet);
        setRealisedReturnGross(totalDollarGross);
        setRealisedPercentageBacktest(totalPercentGross);
        setRealisedReturnBacktest(totalDollarNet);
        setRealisedReturnGrossBacktest(totalDollarGross);
        setRealisedPercentageWithFees(totalPercentNet);
        setRealisedPercentageWithFeesBacktest(totalPercentNet);
        localStorage.setItem("realisedPercentageWithFees", totalPercentNet);
        localStorage.setItem(
          "realisedPercentageWithFeesBacktest",
          totalPercentNet
        );
      };

      switch (rotater) {
        case "3 Months":
          last90Days();
          break;
        case "Monthly":
          last30Days();
          break;
        case "Weekly":
          last7Days();
          break;
        case "24 Hours":
          last24Hours();
          break;
        case "All":
          allTime();
          break;
        case "Start Of Year":
          thisYear();
          break;
        default:
          break;
      }
    };
    runRotaterCals();

    return virtualFields;
  };
  //-----------------------------------------------------------------------------------------//
  const idTradeGenerator = (type) => {
    if (type === "backtest") {
      const id = "backtest-" + Math.random().toString(16).slice(2);
      return id;
    } else if (type === "live") {
      const id = "live-" + Math.random().toString(16).slice(2);
      return id;
    } else if (type === "journal") {
      const id = "journal-" + Math.random().toString(16).slice(2);
      return id;
    }
  };
  const handleSreenMode = (event) => {
    localStorage.setItem("view-mode", event.target.value);
    auth.setViewMode(event.target.value);
  };
  const handleUpdateBalance = async (balance) => {
    const result = await DataFetcher("update-backtest-starting-balance", {
      newBalance: balance,
    });
    if (result) {
      throwMessage("success", "Success");
    }
  };
  const showBacktestTrades = (test) => {
    // localStorage.removeItem("trade-to-view")
    localStorage.setItem("backtest-active", test.testId);
    calculateFields(auth.trades, true, test.testId, undefined, undefined, balanceData.selectedAccountId);
    setShowSpecficTest(test.testId);
  };
  const handleRemove = async () => {
    if (formSubmitted) return;
    setFormSubmitted(true);
    setRemove(false);
    setToRemove([]);

    const result = await DataFetcher("remove-trade", {
      id: toRemove,
      backtestingMode: auth.backtestingMode,
    });
    
    if (result && result.error) {
      // Server returned an error (e.g., trying to delete original trade with duplicates)
      setFormSubmitted(false);
      setRemove(false);
      setToRemove([]);
      throwMessage("error", result.error);
      return;
    }
    
    if (result && result.deletedCount === toRemove.length) {
      if (!auth.backtestingMode) {
        const newArray = await auth.trades
          .filter((obj) => !toRemove.some((obj2) => obj._id === obj2._id))
          .sort((a, b) => {
            return moment(a.created).diff(b.created);
          });
        const newAllTradesArray = await auth.allTrades.filter(
          (obj) => !toRemove.some((obj2) => obj._id === obj2._id)
        );
        auth.setAllTrades(newAllTradesArray);
        auth.setTrades(newArray);
        calculateFields(newArray, false, undefined, undefined, undefined, balanceData.selectedAccountId);
        throwMessage("success", "Deleted");
        if (newArray.length === 0) {
          setRealisedPercentage(0);
          setRealisedReturn(0);
        }
      } else {
        const newAllTradesArray = auth.allTrades.filter(
          (obj) => !toRemove.some((obj2) => obj._id === obj2._id)
        );

        auth.setAllTrades(newAllTradesArray);

        const newArray = auth.trades.filter(
          (obj) => !toRemove.some((obj2) => obj._id === obj2._id)
        );
        auth.setTrades(newArray);

        if (newAllTradesArray.length > 0) {
          calculateFields(newAllTradesArray, true, toRemove[0].testId, undefined, undefined, balanceData.selectedAccountId);
        } else {
          setRealisedPercentageBacktest(0);
          setRealisedReturnBacktest(0);
        }
      }
      setFormSubmitted(false);
    } else {
      setFormSubmitted(false);
      throwMessage("error", "Something went wrong");
    }
  };
  const handleBacktestingMode = (event, width) => {
    // If switching off backtesting mode, perform refresh first
    if (!event.target.checked) {
      // Clear trade and forecast related localStorage
      localStorage.removeItem("linked-trade-ids");
      localStorage.removeItem("trade-to-view");
      localStorage.removeItem("rotater");
      localStorage.removeItem("backtest-active");
    }

    auth.handleBacktestingMode(event.target.checked);
    localStorage.setItem("backtest-mode", event.target.checked);
    setRealisedReturnBacktest(0);
    setRealisedPercentageBacktest(0);
    if (event.target.checked) {
      // Save the current account ID before entering backtesting mode
      localStorage.setItem("pre-backtest-account-id", balanceData.selectedAccountId);
      setShowSpecficTest(localStorage.getItem("backtest-active"));
      calculateFields(
        auth.trades,
        true,
        localStorage.getItem("backtest-active"),
        undefined,
        undefined,
        balanceData.selectedAccountId
      );
    } else {
      // Restore the previous account ID when exiting backtesting mode
      const previousAccountId = localStorage.getItem("pre-backtest-account-id");
      if (previousAccountId) {
        localStorage.setItem("selected-account-id", previousAccountId);
        calculateFields(auth.trades, false, undefined, undefined, undefined, previousAccountId);
        // Dispatch event to notify components of account change
        window.dispatchEvent(new Event("account-changed"));
      } else {
        calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
      }
    }
  };
  const handleArchivingTest = async (test) => {
    const schema = {
      id: test._id,
      field: "archived",
      value: "true",
    };
    if (formSubmitted) return;
    setFormSubmitted(true);
    const result = await DataFetcher("edit-backtest", schema);
    if (result) {
      const filteredArray = allbackTestSchemas.filter(
        (x) => x._id !== result._id
      );

      auth.setAllTrades([]);
      setShowSpecficTest("");
      localStorage.removeItem("backtest-active");
      filteredArray.push(result);
      setAllbackTestSchemas(filteredArray);
      auth.setBacktests(filteredArray);
      setSelectedArchivedTests("");
      setSelectedArchivedTestsBoolean(false);
      setFormSubmitted(false);
    } else {
      setFormSubmitted(false);
      throwMessage("error", "Something went wrong");
    }
  };
  const getBacktests = async (trade) => {
    if (trade.missedTrade) {
      localStorage.setItem("backtest-active", "Missed Trades");
      setShowSpecficTest("Missed Trades");
      setAllbackTestSchemas(auth.backtests);
      calculateFields([trade], true, "Missed Trades", undefined, undefined, balanceData.selectedAccountId);
    } else {
      const testId = trade.testId;
      localStorage.setItem("backtest-active", testId);
      setShowSpecficTest(testId);
      setAllbackTestSchemas(auth.backtests);
      calculateFields([trade], true, testId, undefined, undefined, balanceData.selectedAccountId);
    }
  };
  const handleDeleteTest = async (test) => {
    if (formSubmitted) return;
    setFormSubmitted(true);
    const test_id = JSON.parse(test)[1];
    const toDelete = auth.trades
      .filter(
        (trades) =>
          trades.id.includes("backtest-") && trades.testId.includes(test_id)
      )
      .map((t) => {
        return { _id: t._id };
      });

    const toDeleteJournals = auth.trades
      .filter(
        (trades) =>
          trades.id.includes("backtest-") && trades.testId.includes(test_id)
      )
      .map((t) => {
        return { journalID: t.journalID };
      });

    const schema = {
      id: JSON.parse(test)[0],
      testIds: toDelete,
      journalID: toDeleteJournals,
      backtestingMode: auth.backtestingMode,
    };

    const result = await DataFetcher("remove-backtest", schema);

    if (result) {
      const filteredArray = auth.backtests.filter(
        (x) => x._id !== result.backtestId
      );
      auth.setBacktests(filteredArray);
      setAllbackTestSchemas(filteredArray);
      setSelectedArchivedTests("");
      setSelectedArchivedTestsBoolean(false);
      throwMessage("success", "Backtest Removed");
      setFormSubmitted(false);
    } else {
      setFormSubmitted(false);
      throwMessage("error", "Something went wrong");
    }
  };
  const handleUnArchivedTest = async (test) => {
    if (
      allbackTestSchemas?.filter((tests) => tests.archived?.includes("false"))
        .length <= 2
    ) {
      const schema = {
        id: JSON.parse(test)[0],
        field: "archived",
        value: "false",
      };
      if (formSubmitted) return;
      setFormSubmitted(true);
      const result = await DataFetcher("edit-backtest", schema);

      if (result) {
        const filteredArray = allbackTestSchemas.filter(
          (x) => x._id !== result._id
        );
        filteredArray.push(result);
        setAllbackTestSchemas(filteredArray);
        auth.setBacktests(filteredArray);
        setSelectedArchivedTests("");
        setSelectedArchivedTestsBoolean(false);
        setFormSubmitted(false);
      } else {
        setFormSubmitted(false);
        throwMessage("error", "Something went wrong");
      }
    } else {
      throwMessage("warning", "Limit Reached");
    }
  };
  const handleInProgressMode = (event) => {
    localStorage.removeItem("trade-to-view");
    auth.setInProgressMode(event.target.checked);
    localStorage.setItem("inprogress-mode", event.target.checked);
    if (auth.backtestingMode) {
      calculateFields(
        auth.trades,
        true,
        localStorage.getItem("backtest-active"),
        undefined,
        undefined,
        balanceData.selectedAccountId
      );
    } else {
      calculateFields(auth.trades, false, undefined, undefined, undefined, balanceData.selectedAccountId);
    }
  };
  const handleSimpleView = () => {
    const selectedAccountId = localStorage.getItem("selected-account-id") || "all";
    const availableModes = ["simple-view", "edit-view", "all-view"];

    const currentMode = auth.viewMode;
    const currentIndex = availableModes.indexOf(currentMode);

    let nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + 1) % availableModes.length;

    let nextMode = availableModes[nextIndex];

    // Skip edit-view if "All Accounts" is selected AND not in backtesting mode
    if (nextMode === "edit-view" && selectedAccountId === "All" && !auth.backtestingMode) {
  
      nextIndex = (nextIndex + 1) % availableModes.length;
      nextMode = availableModes[nextIndex];
    }

    auth.setViewMode(nextMode);

    try {
      localStorage.setItem("view-mode", nextMode);
    } catch (e) {
      // ignore storage errors
    }
  };
  const runDurationCal = (params) => {
    // Use pre-calculated duration from database (in hours)
    const hours = params.row.duration || 0;
    
    if (hours === 0) {
      return <p>In Progress</p>;
    }

    const days = (hours / 24).toFixed(1);

    if (!isNaN(hours)) {
      return (
        <p>
          {hours >= 24 ? days : hours.toFixed(1)} {hours >= 24 ? "Days" : "Hrs"}
        </p>
      );
    } else {
      return <p>In Progress</p>;
    }
  };
  const handleRemoveButton = (selectedRowData) => {
    if (selectedRowData.length > 0) {
      setRemove(true);
    } else {
      setRemove(false);
    }
  };

  //-----------------------------------------------------------------------------------------//

  return {
    calculateFields,
    handleSimpleView,
    handleSreenMode,
    allbackTestSchemas,
    showBacktestTrades,
    remove,
    handleRemove,
    handleOpen,
    realisedPercentage,
    realisedReturn,
    realisedReturnGross,
    realisedPercentageWithFees,
    realisedPercentageWithFeesBacktest,
    handleBacktestingMode,
    selectedArchivedTests,
    setSelectedArchivedTests,
    handleOpenBacktest,
    handleOpenEditBacktest,
    editBacktestMode,
    editBacktestData,
    handleArchivingTest,
    handleDeleteTest,
    setSelectedArchivedTestsBoolean,
    selectedArchivedTestsBoolean,
    handleUnArchivedTest,
    showSpecficTest,
    setShowSpecficTest,
    realisedPercentageBacktest,
    realisedReturnBacktest,
    realisedReturnGrossBacktest,
    handleUpdateBalance,
    handleInProgressMode,
    handleEditCell,
    setToRemove,
    toRemove,
    setOpenJournal,
    idTradeGenerator,
    runDurationCal,
    handleRemoveButton,
    handleClose,
    open,
    handleCloseBacktest,
    openNewTest,
    handleCloseJournal,
    openJournal,
    handleOpenJournal,
    setAllbackTestSchemas,
    formSubmitted,
    handleCloseCal,
    openCal,
    handleOpenCal,
    equityCurve,
    setEquityCurve,
    openMacro,
    handleOpenMacro,
    handleCloseMacro,
    openForecast,
    handleOpenForecast,
    handleCloseForecast,
    forecastPrefilledData,
  };
};

export default Logic;

